import { apiSlice } from './apiSlice'

export enum CourtroomStatus {
  SCHEDULED = 'scheduled',
  ACTIVE = 'active',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export interface Courtroom {
  id: string
  round_id: string
  case_id: string
  meeting_link: string
  meeting_id?: string
  status: CourtroomStatus
  participants: string[]
  attendance: Record<string, string> // user_id -> join_time
  recording_url?: string
  recording_source?: string | null
  recording_updated_at?: string | null
  recording_status?: 'idle' | 'recording' | 'processing' | 'ready' | 'failed'
  recording_started_at?: string | null
  recording_ended_at?: string | null
  recording_duration_seconds?: number | null
  recording_size_bytes?: number | null
  recording_format?: string | null
  session_duration_seconds?: number | null
  timer_started_at?: string | null
  timer_paused?: boolean | null
  timer_paused_remaining_seconds?: number | null
  mediation_result?: 'mediation_success' | 'mediation_failed' | 'postponed' | null
  mediation_conclusion?: string | null
  mediation_attachment_document_id?: string | null
  mediation_decided_by?: string | null
  mediation_decided_at?: string | null
  verdict_summary?: string | null
  verdict_detail_html?: string | null
  verdict_winner_team_id?: string | null
  verdict_penalty?: string | null
  verdict_legal_basis?: string | null
  verdict_attachment_document_id?: string | null
  verdict_status?: 'none' | 'submitted' | 'published' | 'rejected' | null
  verdict_decided_by?: string | null
  verdict_decided_at?: string | null
  verdict_published_by?: string | null
  verdict_published_at?: string | null
  created_at?: string
  updated_at?: string
  started_at?: string
  ended_at?: string
  host_judge_id?: string | null
  session_notes?: string | null
  shared_evidence_active?: boolean
  shared_evidence_asset_id?: string | null
  shared_evidence_asset_type?: string | null
  shared_evidence_title?: string | null
  shared_evidence_file_name?: string | null
  shared_evidence_mime_type?: string | null
  shared_by_user_id?: string | null
  shared_by_role?: string | null
  shared_started_at?: string | null
  kicked_participants?: Record<
    string,
    { status: string; kicked_at: string; kicked_by: string; reason?: string }
  >
  muted_participants?: Record<
    string,
    { status: string; muted_at: string; muted_by: string; reason?: string }
  >
  livestream_enabled?: boolean
  livestream_moderator_ids?: string[]
  livestream_watch_url?: string | null
  // ─── Presentation flow (M06-3) ───
  presenter_user_id?: string | null
  presentation_current_page?: number
  presentation_annotations?: Array<{
    id: string
    page: number
    x: number
    y: number
    w: number
    h: number
    color?: string
    by_user_id?: string
    by_role?: string
    created_at?: string
  }>
}

export interface CourtroomCreate {
  round_id: string
  auto_create_meeting?: boolean
  meeting_title?: string
  start_time?: string
  end_time?: string
  host_judge_id?: string | null
  session_notes?: string | null
}

export interface CourtroomTimerState {
  duration_seconds: number
  remaining_seconds: number
  is_paused: boolean
  overtime_seconds: number
  server_now_iso: string
  milestone?: '10m' | '5m' | '1m' | 'time_up' | null
}

export interface CourtroomVerdict {
  status: 'none' | 'submitted' | 'published' | 'rejected'
  summary?: string
  detail_html?: string
  winner_team_id?: string
  penalty?: string
  legal_basis?: string
  attachment_document_id?: string
  decided_by?: string
  decided_at?: string
  published_by?: string
  published_at?: string
}

export interface CourtroomPresenceParticipant {
  user_id: string
  mic_on?: boolean | null
  cam_on?: boolean | null
}

export interface CourtroomPresence {
  room_name?: string | null
  participants: CourtroomPresenceParticipant[]
}

/** Sự kiện macro (append-only) — đồng bộ với GET /courtrooms/{id}/macro-events */
export interface CourtroomMacroEvent {
  id: string
  courtroom_id: string
  event_type: string
  ts: string
  actor_id?: string | null
  actor_role?: string | null
  payload: Record<string, unknown>
}

export const courtroomsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCourtroom: builder.query<Courtroom, string>({
      query: (courtroomId) => `/courtrooms/${courtroomId}`,
      providesTags: (result, error, id) => [{ type: 'Courtroom', id }],
    }),
    getCourtroomByRound: builder.query<Courtroom, string>({
      query: (roundId) => `/courtrooms/round/${roundId}`,
      providesTags: (result, error, roundId) => [
        { type: 'Courtroom' },
        { type: 'Round', id: roundId },
      ],
    }),
    listCourtroomsByRound: builder.query<Courtroom[], string>({
      query: (roundId) => `/courtrooms/round/${roundId}/list`,
      providesTags: (result, error, roundId) => [
        { type: 'Courtroom' },
        { type: 'Round', id: roundId },
      ],
    }),
    createCourtroom: builder.mutation<Courtroom, CourtroomCreate>({
      query: (body) => ({
        url: '/courtrooms/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Courtroom', 'Round'],
    }),
    joinCourtroom: builder.mutation<
      { message: string; courtroom_id: string; meeting_link: string },
      string
    >({
      query: (courtroomId) => ({
        url: `/courtrooms/${courtroomId}/join`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Courtroom', id }],
    }),
    startCourtroom: builder.mutation<
      { message: string; courtroom: Courtroom },
      string
    >({
      query: (courtroomId) => ({
        url: `/courtrooms/${courtroomId}/start`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Courtroom', id }],
    }),
    endCourtroom: builder.mutation<
      { message: string; courtroom: Courtroom },
      { courtroomId: string; result: 'mediation_success' | 'mediation_failed' | 'postponed'; conclusion: string; attachment_document_id?: string }
    >({
      query: ({ courtroomId, ...body }) => ({
        url: `/courtrooms/${courtroomId}/end`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { courtroomId }) => [{ type: 'Courtroom', id: courtroomId }],
    }),
    reopenCourtroom: builder.mutation<
      { message: string; courtroom: Courtroom },
      { courtroomId: string; reason: string }
    >({
      query: ({ courtroomId, reason }) => ({
        url: `/courtrooms/${courtroomId}/reopen`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (result, error, { courtroomId }) => [{ type: 'Courtroom', id: courtroomId }],
    }),
    setRecording: builder.mutation<
      { message: string; recording_url: string },
      { courtroomId: string; recordingUrl: string }
    >({
      query: ({ courtroomId, recordingUrl }) => ({
        url: `/courtrooms/${courtroomId}/recording`,
        method: 'PUT',
        body: { recording_url: recordingUrl },
      }),
      invalidatesTags: (result, error, { courtroomId }) => [
        { type: 'Courtroom', id: courtroomId },
      ],
    }),
    startRecording: builder.mutation<{ message: string; courtroom: Courtroom }, string>({
      query: (courtroomId) => ({
        url: `/courtrooms/${courtroomId}/recording/start`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Courtroom', id }],
    }),
    stopRecording: builder.mutation<
      { message: string; courtroom: Courtroom },
      { courtroomId: string; recording_url?: string; recording_size_bytes?: number; recording_format?: string }
    >({
      query: ({ courtroomId, ...body }) => ({
        url: `/courtrooms/${courtroomId}/recording/stop`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { courtroomId }) => [{ type: 'Courtroom', id: courtroomId }],
    }),
    configureTimer: builder.mutation<
      { message: string; courtroom: Courtroom },
      { courtroomId: string; duration_seconds: number }
    >({
      query: ({ courtroomId, duration_seconds }) => ({
        url: `/courtrooms/${courtroomId}/timer/config`,
        method: 'POST',
        body: { duration_seconds },
      }),
      invalidatesTags: (result, error, { courtroomId }) => [{ type: 'Courtroom', id: courtroomId }],
    }),
    pauseTimer: builder.mutation<{ message: string; courtroom: Courtroom }, string>({
      query: (courtroomId) => ({
        url: `/courtrooms/${courtroomId}/timer/pause`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Courtroom', id }],
    }),
    resumeTimer: builder.mutation<{ message: string; courtroom: Courtroom }, string>({
      query: (courtroomId) => ({
        url: `/courtrooms/${courtroomId}/timer/resume`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Courtroom', id }],
    }),
    getTimerState: builder.query<CourtroomTimerState, string>({
      query: (courtroomId) => `/courtrooms/${courtroomId}/timer/state`,
      providesTags: (result, error, id) => [{ type: 'Courtroom', id }],
    }),
    submitVerdict: builder.mutation<
      { message: string; courtroom: Courtroom },
      {
        courtroomId: string
        summary: string
        detail_html: string
        winner_team_id: string
        penalty?: string
        legal_basis?: string
        attachment_document_id?: string
      }
    >({
      query: ({ courtroomId, ...body }) => ({
        url: `/courtrooms/${courtroomId}/verdict`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { courtroomId }) => [{ type: 'Courtroom', id: courtroomId }],
    }),
    publishVerdict: builder.mutation<{ message: string; courtroom: Courtroom }, string>({
      query: (courtroomId) => ({
        url: `/courtrooms/${courtroomId}/verdict/publish`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Courtroom', id }],
    }),
    getVerdict: builder.query<CourtroomVerdict, string>({
      query: (courtroomId) => `/courtrooms/${courtroomId}/verdict`,
      providesTags: (result, error, id) => [{ type: 'Courtroom', id }],
    }),
    shareEvidence: builder.mutation<
      Courtroom,
      {
        courtroomId: string
        asset_id: string
        asset_type: 'document' | 'memorial' | 'evidence'
        title?: string
        file_name?: string
        mime_type?: string
      }
    >({
      query: ({ courtroomId, ...body }) => ({
        url: `/courtrooms/${courtroomId}/share-evidence`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { courtroomId }) => [{ type: 'Courtroom', id: courtroomId }],
    }),
    stopSharing: builder.mutation<Courtroom, string>({
      query: (courtroomId) => ({
        url: `/courtrooms/${courtroomId}/stop-sharing`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, courtroomId) => [{ type: 'Courtroom', id: courtroomId }],
    }),
    getMeetingToken: builder.mutation<
      {
        token: string
        exp: number
        is_owner: boolean
        user_name: string
        user_id: string
        room_name: string
      },
      string
    >({
      query: (courtroomId) => ({
        url: `/courtrooms/${courtroomId}/meeting-token`,
        method: 'POST',
      }),
    }),
    kickParticipant: builder.mutation<
      Courtroom,
      { courtroomId: string; userId: string; reason?: string }
    >({
      query: ({ courtroomId, userId, reason }) => ({
        url: `/courtrooms/${courtroomId}/participants/${userId}/kick`,
        method: 'POST',
        body: { reason: reason || '' },
      }),
      invalidatesTags: (result, error, { courtroomId }) => [{ type: 'Courtroom', id: courtroomId }],
    }),
    unkickParticipant: builder.mutation<
      Courtroom,
      { courtroomId: string; userId: string }
    >({
      query: ({ courtroomId, userId }) => ({
        url: `/courtrooms/${courtroomId}/participants/${userId}/unkick`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (result, error, { courtroomId }) => [{ type: 'Courtroom', id: courtroomId }],
    }),
    muteParticipant: builder.mutation<
      Courtroom,
      { courtroomId: string; userId: string; reason?: string }
    >({
      query: ({ courtroomId, userId, reason }) => ({
        url: `/courtrooms/${courtroomId}/participants/${userId}/mute`,
        method: 'POST',
        body: { reason: reason || '' },
      }),
      invalidatesTags: (result, error, { courtroomId }) => [{ type: 'Courtroom', id: courtroomId }],
    }),
    unmuteParticipant: builder.mutation<
      Courtroom,
      { courtroomId: string; userId: string }
    >({
      query: ({ courtroomId, userId }) => ({
        url: `/courtrooms/${courtroomId}/participants/${userId}/unmute`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (result, error, { courtroomId }) => [{ type: 'Courtroom', id: courtroomId }],
    }),
    sendPointerEvent: builder.mutation<
      { ok: boolean },
      { courtroomId: string; x: number; y: number; active?: boolean; page?: number }
    >({
      query: ({ courtroomId, ...body }) => ({
        url: `/courtrooms/${courtroomId}/pointer`,
        method: 'POST',
        body,
      }),
    }),
    stopPointerEvent: builder.mutation<{ ok: boolean }, string>({
      query: (courtroomId) => ({
        url: `/courtrooms/${courtroomId}/pointer/stop`,
        method: 'POST',
      }),
    }),
    getRecording: builder.query<{ recording_url: string }, string>({
      query: (courtroomId) => `/courtrooms/${courtroomId}/recording`,
      providesTags: (result, error, id) => [{ type: 'Courtroom', id }],
    }),
    getCourtroomPresence: builder.query<CourtroomPresence, string>({
      query: (courtroomId) => `/courtrooms/${courtroomId}/presence`,
      providesTags: (result, error, id) => [{ type: 'Courtroom', id }],
    }),
    getCourtroomMacroEvents: builder.query<
      CourtroomMacroEvent[],
      { courtroomId: string; limit?: number }
    >({
      query: ({ courtroomId, limit = 500 }) =>
        `/courtrooms/${courtroomId}/macro-events?limit=${encodeURIComponent(String(limit))}`,
      providesTags: (result, error, { courtroomId }) => [{ type: 'Courtroom', id: courtroomId }],
    }),
    // UAT-Chung kết #15: list verdicts cho Ban Thư ký
    listVerdicts: builder.query<
      Array<{
        courtroom_id: string
        case_id: string
        round_id?: string | null
        verdict_status: string
        summary?: string | null
        winner_team_id?: string | null
        decided_by?: string | null
        decided_at?: string | null
        published_by?: string | null
        published_at?: string | null
      }>,
      { status?: 'submitted' | 'published'; case_id?: string } | void
    >({
      query: (params) => {
        const p: Record<string, string> = {}
        if (params && 'status' in params && params.status) p.status = params.status
        if (params && 'case_id' in params && params.case_id) p.case_id = params.case_id
        return { url: '/courtrooms/verdicts/list', params: p }
      },
      providesTags: ['Courtroom'],
    }),
    // UAT-Chung kết #14: BTC inject tình tiết bất ngờ
    injectSurprise: builder.mutation<
      { ok: boolean; event: any },
      {
        courtroomId: string
        actor_name: string
        statement: string
        target_team_id?: string
      }
    >({
      query: ({ courtroomId, ...body }) => ({
        url: `/courtrooms/${courtroomId}/inject-surprise`,
        method: 'POST',
        body,
      }),
    }),
  }),
})

export const {
  useGetCourtroomQuery,
  useGetCourtroomByRoundQuery,
  useListCourtroomsByRoundQuery,
  useCreateCourtroomMutation,
  useJoinCourtroomMutation,
  useStartCourtroomMutation,
  useEndCourtroomMutation,
  useReopenCourtroomMutation,
  useSetRecordingMutation,
  useStartRecordingMutation,
  useStopRecordingMutation,
  useConfigureTimerMutation,
  usePauseTimerMutation,
  useResumeTimerMutation,
  useGetTimerStateQuery,
  useSubmitVerdictMutation,
  usePublishVerdictMutation,
  useGetVerdictQuery,
  useShareEvidenceMutation,
  useStopSharingMutation,
  useKickParticipantMutation,
  useUnkickParticipantMutation,
  useGetMeetingTokenMutation,
  useMuteParticipantMutation,
  useUnmuteParticipantMutation,
  useSendPointerEventMutation,
  useStopPointerEventMutation,
  useGetRecordingQuery,
  useGetCourtroomPresenceQuery,
  useGetCourtroomMacroEventsQuery,
  useListVerdictsQuery,
  useLazyListVerdictsQuery,
  useInjectSurpriseMutation,
} = courtroomsApi
